import { useState, useMemo } from 'react';
import { MenuItem, Select, CircularProgress, TextField } from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { simulateBlockMined } from 'services/games';

const COINS = ['BTC', 'RVN', 'BCH', 'DOGE', 'LTC', 'KAS'];

function formatNum(n, decimals = 8) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  });
}

function formatUsd(n) {
  if (n == null || isNaN(n)) return '—';
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function SimulateBlockMined({ roomId, exchangeData }) {
  const [coin, setCoin] = useState('KAS');
  const [amountBtcOverride, setAmountBtcOverride] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const dataLoaded = exchangeData != null;
  const rates = exchangeData?.rates || {};
  const nativeRewards = exchangeData?.nativeBlockRewards || {};
  const btcEquivalents = exchangeData?.btcEquivalents || {};

  const nativeReward = nativeRewards[coin] ?? 0;
  const coinUsd = rates[coin] ?? 0;
  const btcUsd = rates['BTC'] ?? 0;
  const btcEquiv = btcEquivalents[coin] ?? 0;

  const conversionMath = useMemo(() => {
    if (!dataLoaded) return null;
    if (!nativeReward || !btcUsd) return null;
    if (coin === 'BTC') {
      const usdVal = nativeReward * btcUsd;
      return `${formatNum(nativeReward)} BTC = ${formatUsd(usdVal)}`;
    }
    if (!coinUsd) return `${formatNum(nativeReward, 2)} ${coin} (rate unavailable)`;
    const usdVal = btcEquiv * btcUsd;
    return `${formatNum(nativeReward, 2)} ${coin} × ${formatUsd(coinUsd)} ÷ ${formatUsd(btcUsd)} = ${formatNum(btcEquiv)} BTC (${formatUsd(usdVal)})`;
  }, [dataLoaded, coin, nativeReward, coinUsd, btcUsd, btcEquiv]);

  const handleSimulate = async () => {
    const confirmed = window.confirm(
      `Simulate a ${coin} block mined for room ${roomId}?\n\n` +
        `This will trigger the FULL game flow: winner payouts, leaderboard updates, and WebSocket notifications.`
    );
    if (!confirmed) return;

    setLoading(true);
    setResult(null);
    setError('');
    try {
      const override = amountBtcOverride.trim() !== '' ? amountBtcOverride : null;
      const res = await simulateBlockMined(roomId, coin, override);
      setResult(res);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Simulation failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <MDBox
      sx={{
        border: '1px solid #ffffff1f',
        borderRadius: '4px',
        padding: '8px',
        marginTop: '6px',
        background: 'rgba(255,255,255,0.03)'
      }}
    >
      <MDTypography sx={{ fontSize: '12px', fontWeight: 600, color: '#dbe8ff', marginBottom: '6px' }}>
        Simulate Block Mined
      </MDTypography>

      {!dataLoaded && (
        <MDTypography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
          Loading exchange rates...
        </MDTypography>
      )}

      {dataLoaded && (
        <>
          {/* Coin selector */}
          <MDBox sx={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
            <MDTypography sx={{ fontSize: '12px', color: '#9bc2ff', minWidth: '36px' }}>Coin:</MDTypography>
            <Select
              value={coin}
              onChange={(e) => {
                setCoin(e.target.value);
                setResult(null);
                setError('');
              }}
              size='small'
              sx={{
                minWidth: '100px',
                '& .MuiSelect-select': {
                  color: '#d3ddf5',
                  WebkitTextFillColor: '#d3ddf5',
                  padding: '4px 8px',
                  fontSize: '12px'
                },
                '& .MuiSvgIcon-root': { color: 'rgba(211,221,245,0.82)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff1f' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.45)' }
              }}
            >
              {COINS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </MDBox>

          {/* Conversion info */}
          {conversionMath && (
            <MDTypography sx={{ fontSize: '11px', color: '#7eb8f0', marginBottom: '6px', lineHeight: 1.4 }}>
              Block reward: {conversionMath}
            </MDTypography>
          )}

          {/* BTC override */}
          <MDBox sx={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <MDTypography sx={{ fontSize: '12px', color: '#9bc2ff', minWidth: '80px' }}>Override BTC:</MDTypography>
            <TextField
              size='small'
              type='number'
              placeholder={btcEquiv ? formatNum(btcEquiv) : '(auto)'}
              value={amountBtcOverride}
              onChange={(e) => setAmountBtcOverride(e.target.value)}
              inputProps={{ step: '0.00000001', min: '0.00000001' }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  '& fieldset': { borderColor: '#ffffff1f' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.45)' }
                },
                '& .MuiInputBase-input': {
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: '#e6eefc',
                  WebkitTextFillColor: '#e6eefc'
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(223,232,250,0.5)',
                  opacity: 1
                }
              }}
            />
          </MDBox>

          {/* Mine button */}
          <MDButton
            color='warning'
            size='small'
            fullWidth
            disabled={loading}
            onClick={handleSimulate}
            sx={{ fontSize: '12px' }}
          >
            {loading ? <CircularProgress size={16} color='inherit' /> : 'Mine Block'}
          </MDButton>
        </>
      )}

      {/* Result */}
      {result && (
        <MDBox
          sx={{
            marginTop: '8px',
            padding: '6px 8px',
            borderRadius: '4px',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid #ffffff1f'
          }}
        >
          <MDTypography sx={{ fontSize: '11px', color: '#4ade80', fontWeight: 600 }}>
            Block mined! {formatNum(result.payoutRewardBtc)} BTC ({formatUsd(result.payoutRewardUsd)})
          </MDTypography>
          <MDTypography sx={{ fontSize: '11px', color: '#9bc2ff' }}>
            {result.coin} — {result.winnersCount} winner(s) paid
          </MDTypography>
          <MDTypography sx={{ fontSize: '10px', color: '#7eb8f0' }}>
            Rates: {result.coin}/USD={formatUsd(result.exchangeRatesUsed?.coinUsd)}, BTC/USD=
            {formatUsd(result.exchangeRatesUsed?.btcUsd)}
          </MDTypography>
          <MDTypography sx={{ fontSize: '10px', color: '#6b92c0', wordBreak: 'break-all' }}>
            Hash: {result.blockHash}
          </MDTypography>
        </MDBox>
      )}

      {/* Error */}
      {error && (
        <MDTypography sx={{ fontSize: '11px', color: '#ff8a80', marginTop: '6px' }}>{error}</MDTypography>
      )}
    </MDBox>
  );
}
